import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Import tracker components
import SalatTracker from './SalatTracker';
import QuranTracker from './QuranTracker';
import Checklist from './Checklist';

const TrackerTabs: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'salat' | 'quran' | 'checklist'>('salat');

  // Tab data with icons and titles
  const tabs = [
    {
      id: 'salat',
      title: t('sections.salatTracker'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
        </svg>
      ),
      color: 'text-[#8a744e]',
      bgColor: 'bg-[#e8dab8]',
      hoverBg: 'hover:bg-[#f7f5e8]',
      activeBg: 'bg-[#f7f5e8]',
      component: <SalatTracker />
    },
    {
      id: 'quran',
      title: t('sections.quranTracker'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
        </svg>
      ),
      color: 'text-[#4b7a3c]',
      bgColor: 'bg-[#a6ce86]',
      hoverBg: 'hover:bg-[#f0f8ec]',
      activeBg: 'bg-white',
      component: <QuranTracker />
    },
    {
      id: 'checklist',
      title: t('sections.dailyChecklist'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
          <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
        </svg>
      ),
      color: 'text-[#616e9e]',
      bgColor: 'bg-[#9eb1db]',
      hoverBg: 'hover:bg-[#f0f3fa]',
      activeBg: 'bg-white',
      component: <Checklist />
    }
  ];

  // Find the active tab data
  const activeTabData = tabs.find(tab => tab.id === activeTab);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      {/* Tab navigation */}
      <div className="flex border-b">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as 'salat' | 'quran' | 'checklist')}
            className={`flex-1 flex items-center justify-center py-3 space-x-2 transition-colors duration-200 
              ${tab.id === activeTab 
                ? `${tab.color} ${tab.activeBg} border-b-2 border-${tab.color.replace('text-', '')}`
                : `text-gray-500 ${tab.hoverBg}`
              }`}
          >
            <div className={`rounded-full p-1.5 ${tab.id === activeTab ? tab.bgColor : 'bg-gray-200'}`}>
              {tab.icon}
            </div>
            <span className="font-bengali font-medium text-sm">{tab.title}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-4">
        {activeTabData?.component}
      </div>
    </div>
  );
};

export default TrackerTabs; 